import { createSlice } from '@reduxjs/toolkit'
import { register } from '../../../../../../helpers/reducerRegistry'
import { getDataTable } from '../../../../helpers'
import {
  getRecommendationPortfolio,
  getRecommendationRevenue,
  getRecommendationYearsOfOperation,
  getSavedPortfolio,
} from './thunk'

const slice = createSlice({
  name: 'companyAnalysis/peerAnalysis/recommendationList',
  initialState: {
    isLoading: true,
    prevSelectedCompanyList: [],
    selectedCompanyList: [],

    // Portfolio
    savedPortfolioList: [],
    selectedPortfolioList: [],
    activeSavedPortfolio: {},
    isLoadingPortfolio: true,
    portfolioData: [],
    portfolioDataTable: {},
    portfolioPage: 1,
    portfolioTotalRecords: [],
    portfolioIds: [],

    // Revenue
    isLoadingRevenue: true,
    revenueData: [],
    revenueDataTable: {},
    revenuePage: 1,
    revenueTotalRecords: [],
    revenueIds: [],

    // Years in Operation
    isLoadingYearOfOperation: true,
    yearOfOperationData: [],
    yearOfOperationDataTable: {},
    yearOfOperationPage: 1,
    yearOfOperationTotalRecords: [],
    yearOfOperationIds: [],
  },
  reducers: {
    changeActiveSavedPortfolio: (state, action) => {
      state.activeSavedPortfolio = action.payload
    },
    changeSelectedPortfolioList: (state, action) => {
      state.selectedPortfolioList = action.payload
    },
    changePortfolioPage: (state, action) => {
      state.portfolioPage = action.payload
    },
    changeRevenuePage: (state, action) => {
      state.revenuePage = action.payload
    },
    changeYearOfOperationPage: (state, action) => {
      state.yearOfOperationPage = action.payload
    },
    changeSelectedCompanyList: (state, action) => {
      state.selectedCompanyList = action.payload
    },
    changePrevSelectedCompanyList: (state, action) => {
      state.prevSelectedCompanyList = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getSavedPortfolio.pending, (state) => {
      state.isLoading = true
      state.savedPortfolioList = []
      state.activeSavedPortfolio = {}
    })
    builder.addCase(getSavedPortfolio.fulfilled, (state, { payload = [] }) => {
      state.savedPortfolioList = payload.map((item) => {
        return { label: item.portfolioName, value: item.portfolioId }
      })
      state.activeSavedPortfolio = state.savedPortfolioList.length
        ? state.savedPortfolioList[0]
        : {}
      state.isLoading = false
    })
    builder.addCase(getSavedPortfolio.rejected, (state, { payload = {} }) => {
      state.isLoading = payload.isLoading
    })

    builder.addCase(getRecommendationPortfolio.pending, (state) => {
      state.isLoadingPortfolio = true
      state.portfolioData = []
      state.portfolioDataTable = {}
      state.portfolioTotalRecords = []
      state.portfolioIds = []
    })
    builder.addCase(
      getRecommendationPortfolio.fulfilled,
      (state, { payload = {} }) => {
        state.isLoadingPortfolio = false
        const { data = [], totalRecords = [] } = payload
        state.portfolioTotalRecords = totalRecords
        state.portfolioData = data
        state.portfolioIds = data.map((item) => item.organizationId)
        state.portfolioDataTable = getDataTable(data, state.portfolioIds)
      },
    )
    builder.addCase(
      getRecommendationPortfolio.rejected,
      (state, { payload = {} }) => {
        state.isLoadingPortfolio = payload.isLoading
      },
    )

    builder.addCase(getRecommendationRevenue.pending, (state) => {
      state.isLoadingRevenue = true
      state.revenueTotalRecords = []
      state.revenueData = []
      state.revenueDataTable = {}
      state.revenueIds = []
    })
    builder.addCase(
      getRecommendationRevenue.fulfilled,
      (state, { payload = {} }) => {
        state.isLoadingRevenue = false
        const { data = [], totalRecords = [] } = payload

        state.revenueTotalRecords = totalRecords
        state.revenueData = data
        state.revenueIds = data.map((item) => item.organizationId)
        state.revenueDataTable = getDataTable(data, state.revenueIds)
      },
    )
    builder.addCase(
      getRecommendationRevenue.rejected,
      (state, { payload = {} }) => {
        state.isLoadingRevenue = payload.isLoading
      },
    )

    builder.addCase(getRecommendationYearsOfOperation.pending, (state) => {
      state.isLoadingYearOfOperation = true
      state.yearOfOperationData = []
      state.yearOfOperationDataTable = {}
      state.yearOfOperationTotalRecords = []
      state.yearOfOperationIds = []
    })
    builder.addCase(
      getRecommendationYearsOfOperation.fulfilled,
      (state, { payload = {} }) => {
        state.isLoadingYearOfOperation = false
        const { data = [], totalRecords = [] } = payload

        state.yearOfOperationTotalRecords = totalRecords
        state.yearOfOperationData = data
        state.yearOfOperationIds = data.map((item) => item.organizationId)
        state.yearOfOperationDataTable = getDataTable(
          data,
          state.yearOfOperationIds,
        )
      },
    )
    builder.addCase(
      getRecommendationYearsOfOperation.rejected,
      (state, { payload = {} }) => {
        state.isLoadingYearOfOperation = payload.isLoading
      },
    )
  },
})

// Portfolio
export const selectSavedPortfolioList = (state) =>
  state[slice.name].savedPortfolioList
export const selectActiveSavedPortfolio = (state) =>
  state[slice.name].activeSavedPortfolio

export const selectPortfolioTotalRecords = (state) =>
  state[slice.name].portfolioTotalRecords
export const selectPortfolioPage = (state) => state[slice.name].portfolioPage
export const selectIsLoadingPortfolio = (state) =>
  state[slice.name].isLoadingPortfolio
export const selectPortfolioData = (state) => state[slice.name].portfolioData
export const selectPortfolioIds = (state) => state[slice.name].portfolioIds
export const selectPortfolioDataItem = (rowId, colId) => (state) =>
  state[slice.name].portfolioDataTable[rowId][colId]

// Revenue
export const selectRevenueTotalRecords = (state) =>
  state[slice.name].revenueTotalRecords
export const selectRevenuePage = (state) => state[slice.name].revenuePage
export const selectIsLoadingRevenue = (state) =>
  state[slice.name].isLoadingRevenue
export const selectRevenueData = (state) => state[slice.name].revenueData
export const selectRevenueIds = (state) => state[slice.name].revenueIds
export const selectRevenueDataItem = (rowId, colId) => (state) =>
  state[slice.name].revenueDataTable[rowId][colId]

// Year Of Operation
export const selectYearOfOperationTotalRecords = (state) =>
  state[slice.name].yearOfOperationTotalRecords
export const selectYearOfOperationPage = (state) =>
  state[slice.name].yearOfOperationPage
export const selectIsLoadingYearOfOperation = (state) =>
  state[slice.name].isLoadingYearOfOperation
export const selectYearOfOperationData = (state) =>
  state[slice.name].yearOfOperationData
export const selectYearOfOperationIds = (state) =>
  state[slice.name].yearOfOperationIds
export const selectYearOfOperationDataItem = (rowId, colId) => (state) =>
  state[slice.name].yearOfOperationDataTable[rowId][colId]

// common
export const selectSelectedCompanyList = (state) =>
  state[slice.name].selectedCompanyList
export const selectPrevSelectedCompanyList = (state) =>
  state[slice.name].prevSelectedCompanyList
export const selectSelectedPortfolioList = (state) =>
  state[slice.name].selectedPortfolioList

export const {
  changeActiveSavedPortfolio,
  changeSelectedCompanyList,
  changePortfolioPage,
  changeRevenuePage,
  changeYearOfOperationPage,
  changeSelectedPortfolioList,
  changePrevSelectedCompanyList,
} = slice.actions

register(slice.name, slice.reducer)
